import {
  Drawer,
  DrawerBody,
  Icon,
  Button,
  Skeleton,
  Card,
  ClioLinkButton,
  Tooltip,
} from '@repo/ui';

import { isEmpty } from 'lodash';
import { dateUtil, numberUtil } from '@xtnd/web/shared';
import { DateFormat } from '@xtnd/web/shared/utils/format';
import { useState, useMemo, useCallback } from 'react';

import { useGetAppealList, useGetInvoice, useMatterInvoiceSummaryFilesQuery } from '../../queries';
import { useUpdateWriteOff, useGetWriteOff } from '../../queries/writeOffsQuery';
import { useInvoiceContext } from '../common/InvoiceContext';
import { FileCategory, WriteOffItem, ActionDialogMode } from '../../types';
import { WriteOffStatus, WriteOffCategory } from '@repo/types';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';

import DocumentLibraryModal from '../common/DocumentLibraryModal';
import ActionDialog from '../write-offs/ActionDialog';
import { ClickableFileButton } from '../common/ClickableFileButton';
import { AppealDetailCard } from './AppealDetailCard';

export default function InvoiceDetailDrawer({
  clerkUserMap,
}: Readonly<{
  clerkUserMap: Map<string, string>;
}>) {
  const { isOpen, invoiceId, writeOffId, closeInvoiceDetailDrawer } = useInvoiceContext();
  const { isDark } = useDarkMode();

  const { data: appeals, isFetching: isLoadingAppeals } = useGetAppealList(invoiceId || '', {
    enabled: !!invoiceId,
  });
  const {
    data: invoiceData,
    isFetching: isLoadingInvoice,
    refetch: refetchInvoice,
  } = useGetInvoice(invoiceId || '', {
    enabled: !!invoiceId,
  });

  // Fetch the specific write-off by ID
  const {
    data: writeOffData,
    isFetching: isLoadingWriteOff,
    refetch: refetchWriteOff,
  } = useGetWriteOff(writeOffId || '', {
    enabled: !!writeOffId,
  });

  const { data: filesResponse, isFetching: isFetchingFiles } = useMatterInvoiceSummaryFilesQuery(
    invoiceId || '',
    { enabled: !!invoiceId }
  );

  // Get writeOffStatus from the fetched write-off
  const writeOffStatus = writeOffData?.status as WriteOffStatus | undefined;

  // Build WriteOffItem for ActionDialog from fetched write-off
  const writeOffItemForDialog = useMemo((): WriteOffItem | null => {
    if (!writeOffData || !invoiceData) return null;
    return {
      writeOffId: writeOffData.writeOffId || '',
      invoiceId: invoiceId || '',
      type: writeOffData.type as any,
      category: (writeOffData.category || WriteOffCategory.WRITE_OFF) as WriteOffCategory,
      status: (writeOffData.status || WriteOffStatus.PENDING) as WriteOffStatus,
      amount: writeOffData.amount || 0,
      feesCut: writeOffData.feesCut || 0,
      expensesCut: writeOffData.expensesCut || 0,
      comment: writeOffData.comment,
      createdByName: writeOffData.createdByName || '',
      updatedByName: writeOffData.updatedByName || '',
      invoice: {
        invoiceId: invoiceId || '',
        invoiceNumber: invoiceData.invoiceNumber,
        clientName: invoiceData.clientName,
        matterDisplayNumber: invoiceData.matterDisplayNumber,
        total: invoiceData.total,
        paid: invoiceData.paid,
        creditsIssued: invoiceData.creditsIssued,
        writeOffStatus: writeOffStatus || WriteOffStatus.PENDING,
      },
    };
  }, [writeOffData, invoiceData, invoiceId, writeOffStatus]);

  // Dialog state
  const [dialogMode, setDialogMode] = useState<ActionDialogMode | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = useCallback((mode: ActionDialogMode) => {
    setDialogMode(mode);
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogMode(null);
  }, []);

  // Update write-off mutation
  const { mutateAsync: patchWriteOff } = useUpdateWriteOff({}, true);

  const handleDialogSubmit = useCallback(
    async (payload: any) => {
      const result = await patchWriteOff(payload);
      // Refetch write-off data to get updated status
      refetchWriteOff();
      refetchInvoice();
      return result;
    },
    [patchWriteOff, refetchWriteOff, refetchInvoice]
  );

  const paymentSupportFiles = useMemo(
    () => filesResponse?.data?.filter(file => file.category === FileCategory.PAYMENT_SUPPORT) || [],
    [filesResponse]
  );

  const appealsSupportFiles = useMemo(
    () => filesResponse?.data?.filter(file => file.category === FileCategory.APPEALS_SUPPORT) || [],
    [filesResponse]
  );

  const [isDocumentLibraryOpen, setIsDocumentLibraryOpen] = useState(false);
  const [visiblePaymentFilesCount, setVisiblePaymentFilesCount] = useState(4);

  const isLoading = isLoadingAppeals || isLoadingInvoice || isFetchingFiles || isLoadingWriteOff;

  if (!invoiceId) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onClose={closeInvoiceDetailDrawer}
      title={`Detail - INV #${invoiceData?.invoiceNumber ?? ''}`}
      showCloseButton
      height="lg"
      className="cursor-default dark:bg-xtnd-dark bg-white min-h-[95vh]"
    >
      {isLoading && (
        <DrawerBody>
          <div className="flex flex-col gap-6 text-sm animate-pulse after:content-[''] after:block after:w-full after:border-b after:dark:border-xtnd-dark">
            <div className="flex gap-6">
              {/* LEFT PANEL: Invoice Info + Compact Cards Skeleton */}
              <div className="flex flex-col gap-4 p-4 w-[60%] bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                {/* Invoice Header skeleton */}
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-24 bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-8 w-40 bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-4 w-32 bg-gray-300 dark:bg-gray-600" />
                </div>

                {/* Compact Cards skeleton */}
                <div
                  className="grid gap-3 pt-3 border-t border-gray-200 dark:border-gray-700"
                  style={{ gridTemplateColumns: '1fr auto 2fr' }}
                >
                  {/* Totals Card skeleton */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <Skeleton className="h-3 w-12 mb-2 bg-gray-300 dark:bg-gray-600" />
                    <div className="grid grid-cols-2 gap-2">
                      <Skeleton className="h-4 w-full bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-4 w-full bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-4 w-full bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-4 w-full bg-gray-300 dark:bg-gray-600" />
                    </div>
                  </div>

                  {/* Assigned Card skeleton */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <Skeleton className="h-3 w-16 mb-2 bg-gray-300 dark:bg-gray-600" />
                    <Skeleton className="h-7 w-24 rounded-md bg-gray-300 dark:bg-gray-600" />
                    <Skeleton className="h-3 w-20 mt-1 bg-gray-300 dark:bg-gray-600" />
                  </div>

                  {/* Payment Support Card skeleton */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton className="h-3 w-28 bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-5 w-5 rounded-full bg-gray-300 dark:bg-gray-600" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Skeleton className="h-8 w-full rounded-md bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-8 w-full rounded-md bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-8 w-full rounded-md bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-8 w-full rounded-md bg-gray-300 dark:bg-gray-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL: Quick Actions Skeleton */}
              <div className="flex flex-col gap-3 p-4 w-[40%] bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                {/* Write-Off Amount + Buttons skeleton */}
                <div className="flex items-stretch gap-3">
                  <div className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                    <Skeleton className="h-3 w-24 mb-1 bg-gray-300 dark:bg-gray-600" />
                    <Skeleton className="h-6 w-20 bg-gray-300 dark:bg-gray-600" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-8 w-24 rounded-md bg-gray-300 dark:bg-gray-600" />
                    <Skeleton className="h-8 w-28 rounded-md bg-gray-300 dark:bg-gray-600" />
                  </div>
                </div>

                {/* Comment skeleton */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Skeleton className="h-3 w-32 mb-2 bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-16 w-full rounded-md bg-gray-300 dark:bg-gray-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Appeals Section Skeleton */}
          <div className="flex flex-col gap-4 p-4 text-sm">
            <div className="flex items-center gap-2 mb-4">
              <Skeleton className="h-3 w-16 bg-gray-300 dark:bg-gray-600" />
              <Skeleton className="h-5 w-5 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <Card className="p-4 space-y-4">
              <div className="flex justify-between items-start">
                <Skeleton className="h-6 w-32 rounded-full bg-gray-300 dark:bg-gray-600" />
                <Skeleton className="h-8 w-24 rounded-md bg-gray-300 dark:bg-gray-600" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-3 w-24 bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-5 w-20 bg-gray-300 dark:bg-gray-600" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-28 bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-5 w-24 bg-gray-300 dark:bg-gray-600" />
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <Skeleton className="h-3 w-32 mb-2 bg-gray-300 dark:bg-gray-600" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full bg-gray-300 dark:bg-gray-600" />
                  <Skeleton className="h-4 w-3/4 bg-gray-300 dark:bg-gray-600" />
                </div>
              </div>
            </Card>
          </div>
        </DrawerBody>
      )}

      {!isLoading && (
        <DrawerBody>
          <div className="flex flex-col gap-6 text-sm font-bold text-xtnd-dark dark:text-xtnd-light after:content-[''] after:block after:w-full after:border-b after:dark:border-xtnd-dark">
            <div className="flex gap-6">
              {/* LEFT PANEL (70%): Invoice Info + Assigned + Payment Support */}
              <div
                className={`flex flex-col gap-4 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 ${writeOffStatus ? 'w-[70%]' : 'w-full'}`}
              >
                {/* Invoice Header */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                    INVOICE #{invoiceData?.invoiceNumber}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {invoiceData?.matterDisplayNumber}
                    </span>
                    {invoiceData?.matterId && (
                      <ClioLinkButton id={invoiceData?.matterId} modal="matters" />
                    )}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm font-normal">
                    {invoiceData?.clientName}
                  </div>
                </div>

                {/* Compact Cards Row */}
                <div
                  className="grid gap-3 pt-3 border-t border-gray-200 dark:border-gray-700"
                  style={{ gridTemplateColumns: '1fr auto 2fr' }}
                >
                  {/* Totals Card */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                      TOTALS
                    </span>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Total:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.total || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Paid:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.paid ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Credits:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.creditsIssued ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#F45C47] font-normal">Due:</span>
                        <span className="font-semibold text-[#F45C47]">
                          {numberUtil.formatCurrency(invoiceData?.outstandingBalance || 0)}
                        </span>
                      </div>
                      {/* TODO: We're not sure the data is correct to display yet. Uncomment when we get the correct data from API */}
                      {/* <div className="flex justify-start gap-1 col-span-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                        <span className="text-gray-500 font-normal">Total Approved Write-Off:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.writeOffAmount ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Fees:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.totalFees ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Expenses:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.totalExpenses ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-normal">Discount:</span>
                        <span className="font-semibold">
                          {numberUtil.formatCurrency(invoiceData?.totalDiscountWriteOffs ?? 0)}
                        </span>
                      </div> */}
                    </div>
                  </div>

                  {/* Assigned Card */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                      ASSIGNED
                    </span>
                    <div className="mt-2">
                      {invoiceData?.assignedTo ? (
                        (() => {
                          const clerkEmail = invoiceData.assignedTo;
                          const clerkName = clerkUserMap.get(clerkEmail) || clerkEmail;

                          return (
                            <div className="flex flex-col gap-1 items-start">
                              <span className="inline-flex items-center px-3 py-1 rounded-md text-sm w-fit font-medium border bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
                                <Icon name="HiUser" className="mr-2" />
                                {clerkName}
                              </span>
                              {(invoiceData.assignedBy || invoiceData.assignedAt) && (
                                <div className="text-xs text-gray-500 mt-0.5 font-normal">
                                  {invoiceData.assignedBy && (
                                    <span>
                                      By{' '}
                                      <span className="font-medium">
                                        {clerkUserMap.get(invoiceData.assignedBy) ||
                                          invoiceData.assignedBy}
                                      </span>
                                    </span>
                                  )}
                                  {invoiceData.assignedBy && invoiceData.assignedAt && (
                                    <span> on </span>
                                  )}
                                  {invoiceData.assignedAt && (
                                    <span>
                                      {!invoiceData.assignedBy && 'On '}
                                      {dateUtil?.formatDate(
                                        new Date(invoiceData.assignedAt),
                                        DateFormat.US
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500 font-normal">
                          Not assigned
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Payment Support Card */}
                  <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                        PAYMENT SUPPORT
                      </span>
                      <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold px-1.5 py-0.5 rounded-full">
                        {paymentSupportFiles.length}
                      </span>
                      <Tooltip content="View All">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            setIsDocumentLibraryOpen(true);
                          }}
                          className="text-gray-500 hover:text-gray-900 dark:hover:text-white p-1 -my-1"
                        >
                          <Icon name="HiDocumentReport" size="sm" />
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="mt-2">
                      {paymentSupportFiles.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          <div className="grid grid-cols-2 gap-2">
                            {paymentSupportFiles.slice(0, visiblePaymentFilesCount).map(file => (
                              <ClickableFileButton key={file.fileId} file={file} />
                            ))}
                          </div>
                          {paymentSupportFiles.length > 4 && (
                            <div className="flex justify-start">
                              {visiblePaymentFilesCount < paymentSupportFiles.length ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setVisiblePaymentFilesCount(paymentSupportFiles.length);
                                  }}
                                  className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-xs px-0"
                                >
                                  +{paymentSupportFiles.length - visiblePaymentFilesCount} more
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setVisiblePaymentFilesCount(4);
                                  }}
                                  className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-xs px-0"
                                >
                                  Show less
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic font-normal">No files</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL (30%): Quick Actions - only shown when writeOffStatus exists */}
              {writeOffStatus && (
                <div className="flex flex-col gap-3 p-4 w-[40%] bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  {/* Write-Off Amount and Action Buttons - side by side */}
                  <div className="flex items-stretch gap-3">
                    {/* Write-Off Amount */}
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/40">
                      <div className="text-[10px] uppercase text-[#F45C47] font-bold tracking-wider">
                        Write Off Amount
                      </div>
                      <div className="text-xl font-bold text-[#F45C47]">
                        {numberUtil.formatCurrency(writeOffData?.amount || 0)}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {/* PENDING: Approve + Request Review (stacked) */}
                    {writeOffStatus === WriteOffStatus.PENDING && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          leftIcon={<Icon name="HiCheckCircle" />}
                          onClick={() => openDialog(WriteOffStatus.APPROVED)}
                          disabled={!writeOffItemForDialog}
                          className="h-full"
                          style={{
                            backgroundColor: '#16a34a',
                            borderColor: '#16a34a',
                            color: 'white',
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          leftIcon={<Icon name="HiExclamationCircle" />}
                          onClick={() => openDialog(WriteOffStatus.ESCALATED)}
                          disabled={!writeOffItemForDialog}
                          className="h-full"
                        >
                          Request Review
                        </Button>
                      </div>
                    )}

                    {/* ESCALATED: Approve + Reject & Restart (stacked) */}
                    {writeOffStatus === WriteOffStatus.ESCALATED && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          leftIcon={<Icon name="HiCheckCircle" />}
                          onClick={() => openDialog(WriteOffStatus.APPROVED)}
                          disabled={!writeOffItemForDialog}
                          className="h-full"
                          style={{
                            backgroundColor: '#16a34a',
                            borderColor: '#16a34a',
                            color: 'white',
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          leftIcon={<Icon name="HiBan" />}
                          onClick={() => openDialog(WriteOffStatus.REJECTED)}
                          disabled={!writeOffItemForDialog}
                          className="h-full"
                        >
                          Reject & Restart
                        </Button>
                      </div>
                    )}

                    {/* APPROVED: Mark Processed */}
                    {writeOffStatus === WriteOffStatus.APPROVED && (
                      <Button
                        size="sm"
                        leftIcon={<Icon name="HiArchive" />}
                        onClick={() => openDialog(WriteOffStatus.ARCHIVED)}
                        disabled={!writeOffItemForDialog}
                        className="h-auto"
                        style={{
                          backgroundColor: '#2563eb',
                          borderColor: '#2563eb',
                          color: 'white',
                        }}
                      >
                        Mark Processed
                      </Button>
                    )}
                  </div>

                  {/* Write-off Comment Display - below actions */}
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                        Write Off Comment
                      </span>
                      <Tooltip content="Edit Comment">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog('note')}
                          disabled={!writeOffItemForDialog}
                          className="text-gray-500 hover:text-gray-900 dark:hover:text-white p-1 -my-1"
                        >
                          <Icon name="HiPencil" size="sm" />
                        </Button>
                      </Tooltip>
                    </div>
                    {writeOffData?.comment ? (
                      <div className="mt-1 p-2 bg-white dark:bg-gray-900/50 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300 font-normal whitespace-pre-wrap">
                          {writeOffData.comment}
                        </p>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 italic font-normal mt-1 block">
                        No comment
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DocumentLibraryModal
              isOpen={isDocumentLibraryOpen}
              onClose={() => setIsDocumentLibraryOpen(false)}
              invoiceId={invoiceId || ''}
            />
          </div>

          <div className="flex flex-col gap-4 p-4 text-sm">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                  APPEALS
                </span>
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {appeals?.length || 0}
                </span>
              </div>
              {isEmpty(appeals) ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Icon name="HiDocumentReport" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No appeals found for this invoice.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {appeals?.map((appeal: any) => (
                    <AppealDetailCard
                      key={appeal.appealId}
                      appeal={appeal}
                      invoiceData={invoiceData}
                      appealsSupportFiles={appealsSupportFiles}
                      writeOffStatus={writeOffStatus}
                      isDark={isDark}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DrawerBody>
      )}

      {/* Action Dialog for write-off actions */}
      {dialogMode && (
        <ActionDialog
          isOpen={isDialogOpen}
          onClose={closeDialog}
          writeOff={writeOffItemForDialog}
          mode={dialogMode}
          onSubmit={handleDialogSubmit}
        />
      )}
    </Drawer>
  );
}
